import { Chip } from "@heroui/react";
import { ImageOff } from "lucide-react";
import { useState } from "react";

import { Product } from "@/types";

export interface ProductCellProps {
  product: Product;
  onImageClick: () => void;
}

export function ProductCell({ product, onImageClick }: ProductCellProps) {
  const imageCount = product.images?.length || 0;
  const src = product.images?.[0];
  const [failed, setFailed] = useState<string | null>(null);
  const broken = !src || failed === src;

  return (
    <div className="flex min-w-0 items-center gap-3">
      {/* A mouse shortcut to the gallery: the image stays in the row (named after the product), not a tab stop. */}
      <div
        className="bg-surface-secondary text-muted relative flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-xl"
        onClick={onImageClick}
      >
        {broken ? (
          <ImageOff aria-hidden="true" className="size-4" />
        ) : null}
        {src ? (
          <img
            alt={product.name}
            className={`absolute inset-0 size-full rounded-xl object-cover ${
              broken ? "opacity-0" : ""
            } ${product.inStock ? "" : "opacity-60 grayscale"}`}
            src={src}
            onError={() => setFailed(src)}
          />
        ) : null}
        {imageCount > 1 ? (
          <span className="bg-foreground text-background absolute -right-1 -bottom-1 rounded-full px-1.5 text-[10px] leading-4 font-medium">
            +{imageCount - 1}
          </span>
        ) : null}
      </div>
      <div className="flex min-w-0 flex-col gap-0.5 leading-tight">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium">{product.name}</span>
          {!product.inStock ? (
            <Chip color="danger" size="sm" variant="soft">
              Out of stock
            </Chip>
          ) : null}
          {product.tags?.map((tag) => (
            <Chip key={tag} size="sm" variant="soft">
              {tag}
            </Chip>
          ))}
        </div>
        <span className="text-muted self-start truncate text-xs" dir="rtl">
          {product.nameAr}
        </span>
      </div>
    </div>
  );
}
